<template>
    <div class="session-duration-chart" :class="{ fullscreen: fullScreen }">
        <h2 v-if="fullScreen">Session Duration Overall</h2>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton :classNames="tableMode ? '' : 'selected'" :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }">
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton :classNames="tableMode ? 'selected' : ''" :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }">
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="middle">
                <label>{{ $t("report.aggregateAs") }}:</label>
                <a-radio-group size="small" v-model="aggregationType">
                    <a-radio-button value="avg"> {{ $t("message.average") }}</a-radio-button>
                    <a-radio-button value="total"> {{ $t("report.total") }}</a-radio-button>
                </a-radio-group>
                <label>{{ $t("report.handledBy") }}</label>
                <a-radio-group size="small" v-model="handleType">
                    <a-radio-button value="hybrid">{{ $t("report.hybrid") }}</a-radio-button>
                    <a-radio-button value="chatbot">{{ $t("message.chatBot") }}</a-radio-button>
                    <a-radio-button value="agent"> {{ $t("billing.agent") }}</a-radio-button>
                </a-radio-group>
            </div>
            <div class="right">
                <label>{{ $t("report.viewUnitAs") }}:</label>
                <a-radio-group size="small" v-model="unit">
                    <a-radio-button value="seconds"> {{ $t("report.second") }}</a-radio-button>
                    <a-radio-button value="minutes"> {{ $t("report.minute") }}</a-radio-button>
                    <a-radio-button value="hours"> {{ $t("report.hour") }}</a-radio-button>
                </a-radio-group>
            </div>
        </div>
        <TableView v-if="tableMode" :cols="getCols()" :rows="getRows()" />
        <StackedAreaChart
            :title="getReportTitle()"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-else
        />
        <div class="stats">
            <StatCard :title="$t('report.totalSessionDuration')" :value="humanizeDuration(stats.TotalDuration, unit)"
                v-if="aggregationType === 'total'" />
            <StatCard :title="$t('report.totalDurationByCB')"
                :value="humanizeDuration(stats.ChatbotTotalDuration, unit)"
                v-if="(handleType === 'chatbot' || handleType === 'hybrid') && aggregationType === 'total'" />
            <StatCard :title="$t('report.totalDurationByAgent')"
                :value="humanizeDuration(stats.AgentTotalDuration, unit)"
                v-if="(handleType === 'agent' || handleType === 'hybrid') && aggregationType === 'total'" />
            <StatCard :title="$t('report.averageSessionDuration')"
                :value="humanizeDuration(stats.AverageDuration, unit)" v-if="aggregationType === 'avg'" />
            <StatCard :title="$t('report.averageDurationByCB')"
                :value="humanizeDuration(stats.ChatbotAverageDuration, unit)"
                v-if="(handleType === 'chatbot' || handleType === 'hybrid') && aggregationType === 'avg'" />
            <StatCard :title="$t('report.averageDurationByAgent')"
                :value="humanizeDuration(stats.AgentAverageDuration, unit)"
                v-if="(handleType === 'agent' || handleType === 'hybrid') && aggregationType === 'avg'" />
            <StatCard :title="$t('report.maxDuration')" :value="humanizeDuration(stats.MaxDuration, unit)" />
            <StatCard :title="$t('report.minDuration')" :value="humanizeDuration(stats.MinDuration, unit)" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import { expertToExcel } from "../../../utils/utils";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import fscreen from "fscreen";

const log = log2("OverallSessionDurationChart");
export default Vue.extend({
    name: "OverallSessionDurationChart",
    components: { IconButton, StatCard, StackedAreaChart, TableView },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.duration")],
            stats: {
                TotalDuration: 0,
                ChatbotTotalDuration: 0,
                AgentTotalDuration: 0,
                AverageDuration: 0,
                ChatbotAverageDuration: 0,
                AgentAverageDuration: 0,
                MaxDuration: 0,
                MinDuration: 0,
            },
            aggregationType: "avg",
            handleType: "hybrid",
            unit: "minutes",
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            fullScreen: false,
            cancels: [],
        };
    },
    methods: {
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.sessionDurationOverall(this.filters)
                .then((res) => {
                    log("sessionOverall data fetched", res);
                    this.graphData = res;
                    this.getStats();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        humanizeDuration(totalSeconds, type = 'default') {
            const conversionRates = {
                seconds: 1,
                minutes: 60,
                hours: 3600,
                days: 86400,
            };

            if (type in conversionRates) {
                const convertedValue = totalSeconds / conversionRates[type];

                const literals = {
                    day: this.$i18n.locale === "tr" ? "gün" : "day",
                    hour: this.$i18n.locale === "tr" ? "saat" : "hour",
                    min: this.$i18n.locale === "tr" ? "dk" : "min",
                    sec: this.$i18n.locale === "tr" ? "sn" : "sec",
                };

                switch (type) {
                    case 'seconds':
                        return `${Math.floor(convertedValue)} ${literals.sec}`;
                    case 'minutes':
                        return `${Math.floor(convertedValue)} ${literals.min}`;
                    case 'hours':
                        return `${Math.floor(convertedValue)} ${literals.hour}`;
                    case 'days':
                        return `${Math.floor(convertedValue)} ${literals.day}`;
                    default:
                        return `${Math.floor(convertedValue)} ${literals.sec}`;
                }
            }
        },
        getReportTitle() {
            const titles = {
                hybrid: {
                    avg: this.$t("report.averageSessionDuration"),
                    total: this.$t("report.totalSessionDuration"),
                },
                chatbot: {
                    avg: this.$t("report.averageDurationByCB"),
                    total: this.$t("report.totalDurationByCB"),
                },
                agent: {
                    avg: this.$t("report.averageDurationByAgent"),
                    total: this.$t("report.totalDurationByAgent"),
                },
            };
            return titles[this.handleType][this.aggregationType];
        },
        getStats() {
            const { buckets } = this.graphData;

            return buckets.reduce(
                (acc, bucket) => {
                    return {
                        TotalDuration: acc.TotalDuration + bucket.totalDuration,
                        ChatbotTotalDuration: acc.ChatbotTotalDuration + bucket.chatbotTotalDuration,
                        AgentTotalDuration: acc.AgentTotalDuration + bucket.agentTotalDuration,
                        AverageDuration: acc.AverageDuration + bucket.avgDuration,
                        ChatbotAverageDuration: acc.ChatbotAverageDuration + bucket.avgChatbotDuration,
                        AgentAverageDuration: acc.AgentAverageDuration + bucket.avgAgentDuration,
                        MaxDuration: acc.MaxDuration > bucket.totalDuration ? acc.MaxDuration : bucket.totalDuration,
                        MinDuration: acc.MinDuration < bucket.totalDuration ? acc.MinDuration : bucket.totalDuration,
                    };
                },
                {
                    TotalDuration: 0,
                    ChatbotTotalDuration: 0,
                    AgentTotalDuration: 0,
                    AverageDuration: 0,
                    ChatbotAverageDuration: 0,
                    AgentAverageDuration: 0,
                    MaxDuration: 0,
                    MinDuration: 0,
                }
            );
        },

        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        readValueFromBucket(bucket, applyDurationUnit = true) {
            const unitRation = {
                seconds: 1 * 1000,
                minutes: 60 * 1000,
                hours: 60 * 60 * 1000,
            }[this.unit];
            const values = {
                hybrid: {
                    avg: bucket.avgDuration,
                    total: bucket.totalDuration,
                },
                chatbot: {
                    avg: bucket.avgChatbotDuration,
                    total: bucket.chatbotTotalDuration,
                },
                agent: {
                    avg: bucket.avgAgentDuration,
                    total: bucket.agentTotalDuration,
                },
            };
            const valueInMilliSecond = values[this.handleType][this.aggregationType];
            const value = applyDurationUnit ? valueInMilliSecond / unitRation : valueInMilliSecond;

            return value || 0;
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.graphData;
            console.log("Merhabalarefenim", this.graphData);

            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    return this.readValueFromBucket(bucket);
                });
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: "rgb(255, 0, 135)",
                            },
                            {
                                offset: 1,
                                color: "rgb(135, 0, 157)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        getCols() {
            return [
                {
                    title: this.$t("message.date"),
                    dataIndex: "date",
                    key: "date",
                    width: 250,
                    fixed: "left",
                },
                {
                    title: this.getReportTitle(),
                    dataIndex: "value",
                    key: "value",
                    width: 100,
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((bucket) => {
                const value = this.readValueFromBucket(bucket, false) / 1000;
                log(`getRows.readValueFromBucket ${bucket.data} => ${value}`);
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date,
                    value: this.humanizeDuration(value, this.unit),
                };
            });
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;

            const data = buckets.map((bucket) => {
                const unitRation = {
                    seconds: 1 * 1000,
                    minutes: 60 * 1000,
                    hours: 60 * 60 * 1000,
                }[this.unit];
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return {
                    date: date,
                    "Toplam Süre": this.humanizeDuration(bucket.totalDuration / unitRation, this.unit),
                    "Ortalama Süre": this.humanizeDuration(bucket.averageDuration / unitRation, this.unit),
                    "Toplam Süre (Chatbot)": this.humanizeDuration(bucket.chatbotTotalDuration / unitRation, this.unit),
                    "Ortalama Süre (Chatbot)": this.humanizeDuration(bucket.avgChatbotDuration / unitRation, this.unit),
                    "Toplam Süre (Temsilci)": this.humanizeDuration(bucket.agentTotalDuration / unitRation, this.unit),
                    "Ortalama Süre (Temsilci)": this.humanizeDuration(bucket.avgAgentDuration / unitRation, this.unit),
                };
            });

            expertToExcel(data, `session_duration_timeseries.xlsx`, "Sheet 1");
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".session-duration-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".rating-chart"),
            name: "wave",
        });
        this.fetch();
        const fullscreenCallback = () => {
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>

<style lang="scss">
.session-duration-chart {
    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.session-duration-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
